import {
  LikeOutlined,
  DislikeOutlined,
  ExpandOutlined,
  CompressOutlined,
  CopyOutlined
} from "@ant-design/icons";
import { Button, Table, Modal, Tooltip } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useState, useCallback, useMemo } from "react";

import CreateEditCodeModal from "./CreateCodeModal";
import { copyTextToClipboard } from "../../../../utils/clipboardHelper";
import { showErrorNotification, showNotification } from "../../../../utils/notification";

const PREVIEW_LENGTH = 150;

const CodeDisplay = ({ code, onExpand, isExpanded, onCopy }) => {
  const previewCode = useMemo(() => {
    if (!code || code.length <= PREVIEW_LENGTH) return code;
    return code.substring(0, PREVIEW_LENGTH) + "...";
  }, [code]);

  const displayCode = isExpanded ? code : previewCode;
  const needsExpansion = code && code.length > PREVIEW_LENGTH;

  const handleCopyCode = useCallback(async () => {
    const hasCopied = await copyTextToClipboard(code);
    if (hasCopied) {
      onCopy?.();
    }
  }, [code, onCopy]);

  return (
    <div className="code-display-container">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "8px",
          padding: "8px 12px",
          backgroundColor: "#f8f9fa",
          borderRadius: "6px 6px 0 0",
          borderBottom: "1px solid #e9ecef"
        }}
      >
        <div style={{ display: "flex", gap: "4px" }}>
          <Tooltip title="Copy code">
            <Button
              type="text"
              size="medium"
              icon={<CopyOutlined />}
              onClick={handleCopyCode}
              style={{ padding: "4px 8px" }}
            />
          </Tooltip>
          {/* Expand Collape button */}
          {needsExpansion && (
            <Tooltip title={isExpanded ? "Collapse" : "Expand"}>
              <Button
                type="text"
                size="medium"
                icon={isExpanded ? <CompressOutlined /> : <ExpandOutlined />}
                onClick={onExpand}
                style={{ padding: "4px 8px" }}
              />
            </Tooltip>
          )}
        </div>
      </div>

      {/* Code Content */}
      <div
        style={{
          backgroundColor: "#1e1e1e",
          borderRadius: "0 0 6px 6px",
          overflow: "hidden"
        }}
      >
        <pre
          style={{
            margin: 0,
            padding: "16px",
            color: "#d4d4d4",
            // eslint-disable-next-line quotes
            fontFamily: 'Monaco, Menlo, "Ubuntu Mono", Consolas, monospace',
            fontSize: "13px",
            lineHeight: "1.5",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            maxHeight: isExpanded ? "none" : "200px",
            overflow: isExpanded ? "visible" : "hidden",
            transition: "max-height 0.3s ease-in-out"
          }}
        >
          <code>{displayCode}</code>
        </pre>

        {/* Fade overlay for collapsed state */}
        {!isExpanded && needsExpansion && (
          <div
            style={{
              position: "relative",
              height: "40px",
              background: "linear-gradient(transparent, #1e1e1e)",
              marginTop: "-40px",
              display: "flex",
              alignItems: "end",
              justifyContent: "center",
              paddingBottom: "8px"
            }}
          >
            <Button type="link" size="small" onClick={onExpand} style={{ color: "#569cd6" }}>
              Show more
            </Button>
          </div>
        )}
      </div>
      <div
        style={{
          padding: "8px 12px",
          backgroundColor: "#f8f9fa",
          borderTop: "1px solid #e9ecef",
          borderRadius: "0 0 6px 6px",
          fontSize: "12px",
          color: "#6c757d"
        }}
      >
        <span>{code?.length || 0} characters</span>
        {code && <span style={{ marginLeft: "12px" }}>{code.split("\n").length} lines</span>}
      </div>
    </div>
  );
};

const ManageCodesModal = ({
  isModalOpen = false,
  ROOT_URL,
  currentProblemRow = {},
  setProblemsList = () => {},
  setIsModalOpen = () => {}
}) => {
  const { feedback = {}, rating, name, key: problemId } = currentProblemRow;
  const { totalCodesDislikes = 0, totalCodesLikes = 0 } = feedback;

  const [codes, setCodes] = useState([]);
  const [isCreateCodeModalOpen, setIsCreateCodeModalOpen] = useState(false);
  const [initialValues, setInitialValues] = useState({});
  const [expandedCodes, setExpandedCodes] = useState(new Set());

  const updateProblemList = (updatedCodes) => {
    setProblemsList((prevTasks) =>
      prevTasks.map((problem) =>
        problem.key === problemId ? { ...problem, codes: updatedCodes } : problem
      )
    );
  };

  const handleCodeDelete = async (codeId) => {
    try {
      const response = await axios.post(`${ROOT_URL}/admin/cp-sheet/problems/codes/delete`, {
        codeId,
        problemId
      });
      const { updatedCodes } = response.data;
      setCodes(updatedCodes);
      updateProblemList(updatedCodes);
      showNotification("Success", "Code deleted successfully", "success");
    } catch (error) {
      showErrorNotification(error);
    }
  };

  const handleCreateEditCode = async (codeParams) => {
    try {
      const isEdit = initialValues?._id !== undefined;
      const url = isEdit
        ? `${ROOT_URL}/admin/cp-sheet/problems/codes/edit`
        : `${ROOT_URL}/admin/cp-sheet/problems/codes/create`;
      const payload = isEdit
        ? { codeParams, problemId, codeId: initialValues._id }
        : { codeParams, problemId };

      const response = isEdit ? await axios.put(url, payload) : await axios.post(url, payload);
      const { updatedCodes } = response.data;
      setCodes(updatedCodes);
      updateProblemList(updatedCodes);
      setIsCreateCodeModalOpen(false);
      setInitialValues({});
      showNotification("Success", `Code ${isEdit ? "updated" : "created"} successfully`, "success");
    } catch (error) {
      showErrorNotification(error);
    }
  };

  const handleExpandCode = useCallback((codeId) => {
    setExpandedCodes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(codeId)) {
        newSet.delete(codeId);
      } else {
        newSet.add(codeId);
      }
      return newSet;
    });
  }, []);

  const handleCopySuccess = useCallback(() => {
    showNotification("Success", "Code copied to clipboard", "success");
  }, []);

  const columns = [
    {
      title: "Language",
      dataIndex: "language",
      key: "language",
      width: 120
    },
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      render: (code, record) => (
        <CodeDisplay
          code={code}
          language={record.language}
          isExpanded={expandedCodes.has(record._id)}
          onExpand={() => handleExpandCode(record._id)}
          onCopy={handleCopySuccess}
        />
      )
    },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 180,
      render: (updatedAt) => (updatedAt ? dayjs(updatedAt).format("DD MMM YYYY | HH:mm") : "--")
    },
    {
      title: "Actions",
      key: "action",
      width: 160,
      render: (record) => (
        <div
          style={{
            display: "flex",
            gap: "8px",
            justifyContent: "center"
          }}
        >
          <Button
            type="primary"
            onClick={() => {
              setInitialValues(record);
              setIsCreateCodeModalOpen(true);
            }}
          >
            Edit
          </Button>
          <Button type="primary" danger onClick={() => handleCodeDelete(record._id)}>
            Delete
          </Button>
        </div>
      )
    }
  ];

  useEffect(() => {
    setCodes(currentProblemRow.codes || []);
    setExpandedCodes(new Set());
  }, [currentProblemRow.codes]);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setExpandedCodes(new Set());
    setInitialValues({});
  }, [setIsModalOpen]);

  return (
    <Modal
      title={`${rating} | ${name}`}
      centered
      open={isModalOpen}
      footer={null}
      onCancel={handleModalClose}
      width="90vw"
      style={{ maxWidth: "1200px" }}
      destroyOnClose
    >
      <div
        style={{
          marginBottom: "16px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <Button
          type="primary"
          onClick={() => {
            setInitialValues({});
            setIsCreateCodeModalOpen(true);
          }}
        >
          Create New Code
        </Button>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
            fontSize: "14px"
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <LikeOutlined style={{ color: "#52c41a" }} />
            <span>{totalCodesLikes}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <DislikeOutlined style={{ color: "#ff4d4f" }} />
            <span>{totalCodesDislikes}</span>
          </div>
        </div>
      </div>
      <div>
        <b>User Code feedback is considered if it was marked after the code was last updated.</b>
      </div>
      <Table
        columns={columns}
        dataSource={codes}
        rowKey="_id"
        pagination={false}
        bordered
        scroll={{ x: "max-content" }}
      />
      {isCreateCodeModalOpen && (
        <CreateEditCodeModal
          isModalOpen={isCreateCodeModalOpen}
          setIsModalOpen={setIsCreateCodeModalOpen}
          initialValues={initialValues}
          isEdit={initialValues?._id !== undefined}
          handleCreateEdit={handleCreateEditCode}
        />
      )}
    </Modal>
  );
};

export default ManageCodesModal;
